import { Suspense, lazy, useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import { useLocation } from 'react-router';
import { useSharedSource } from '@/modules/room/useSharedSource';
import { MobileNav } from './MobileNav';
import { Sidebar } from './Sidebar';

/**
 * Loaded only when opened.
 *
 * The panel runs a frame timing loop, and that should not cost anything for
 * people who never open it.
 */
const DiagnosticsPanel = lazy(async () => ({
  default: (await import('@/features/diagnostics/DiagnosticsPanel')).DiagnosticsPanel,
}));

/**
 * The frame every screen sits inside.
 *
 * The sidebar lives outside the scroll container so it never moves, and the
 * main region owns its own scrolling. That separation is what allows a route
 * to go full bleed, which the player needs, without fighting the layout.
 */
export function AppShell() {
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  // Mounted at the shell rather than in the player, because the film the room
  // chose has to open even when this person is sitting on the home screen.
  useSharedSource();

  // Ctrl+Shift+D. Chosen because it collides with nothing the player claims,
  // and because a developer panel should not be reachable by a stray keypress.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && (event.key === 'D' || event.key === 'd')) {
        event.preventDefault();
        setDiagnosticsOpen((open) => !open);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // The player is full bleed and owns the whole screen. A navigation bar over
  // the bottom of a film is the wrong trade on a phone, and the player has its
  // own way back.
  const { pathname } = useLocation();
  const immersive = pathname === '/watch';

  return (
    <div className="flex h-full w-full flex-col overflow-hidden bg-bg-base md:flex-row">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-[var(--radius-md)] focus:bg-accent focus:px-4 focus:py-2 focus:text-text-on-accent"
      >
        Skip to content
      </a>

      <div className="hidden md:flex md:h-full">
        <Sidebar />
      </div>

      <main
        id="main"
        className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
        // Named so the skip link has somewhere to land.
        tabIndex={-1}
        // The notch, on a phone held in landscape, eats into the left or right
        // edge rather than the top. Applied here so a route's own padding does
        // not have to know about it.
        style={{
          paddingLeft: 'var(--safe-left)',
          paddingRight: 'var(--safe-right)',
          ...(immersive ? {} : { paddingTop: 'var(--safe-top)' }),
        }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>

      {!immersive && <MobileNav />}

      {diagnosticsOpen && (
        <Suspense fallback={null}>
          <DiagnosticsPanel onClose={() => setDiagnosticsOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Shown while a lazily loaded route chunk is fetched.
 *
 * Deliberately almost nothing. A spinner that appears for 40 ms and vanishes
 * reads as a flicker and makes the application feel less responsive than
 * showing calm empty space for the same 40 ms.
 */
function RouteFallback() {
  return <div className="h-full w-full" aria-busy="true" aria-live="polite" />;
}
